import { Tuple } from "record-tuple";

import UError from "./UError.mts";
import UTuple from "./UTuple.mts";

namespace UFunction {
  export type Maybe<Args extends any[], T> = ((...args: Args) => T) | T;
  export type Unwrap<T extends Maybe<any, any>> = T extends Any
    ? ReturnType<T>
    : T;

  export type Any = (...args: any) => any;
  export namespace Any {
    export type Void = (...args: any) => void;
    export type WithArgCountOf<Count extends number> = (
      ...args: UTuple.Of<any, Count>
    ) => any;
  }

  export type OptionalArgs<Fn extends Any> = (
    ...args: UTuple.Partial<Parameters<Fn>>
  ) => ReturnType<Fn>;

  export function is(maybeFn: any): maybeFn is Any {
    return typeof maybeFn === "function";
  }
  is.some = (...maybeFns: any[]) => maybeFns.some(is);

  const never = Symbol("never");
  export function once<Fn extends UFunction.Any>(fn: Fn) {
    let res = never;

    return ((...args) => {
      if (res === never) res = fn(...args);
      return res;
    }) as Fn;
  }

  export const immediate = <Fn extends UFunction.Any>(fn: Fn): ReturnType<Fn> =>
    fn();

  export function memo<Fn extends UFunction.Any>(
    fn: Fn,
    { by = Tuple.from as (args: Parameters<Fn>) => any } = {}
  ) {
    type Args = any[];
    type Result = any;
    // TODO: Use WeakMap here, holding arg references is fine when function is
    // ephemeral, but otherwise this memory will grow for long lived functions.
    // Impelment separate memo.deep that requires invalidation
    const cache = new Map<Args, Result>();

    return Object.assign(
      ((...args: Parameters<Fn>) => {
        const ref = by(args);

        if (!cache.has(ref)) cache.set(ref, fn(...args));

        return cache.get(ref);
      }) as Fn,
      // Pass through properties attached to fn
      fn,
      {
        peek(...args: Parameters<Fn>) {
          return cache.get(Tuple.from(args));
        },
        invalidate: Object.assign(
          (...args: Parameters<Fn>) => {
            return cache.delete(Tuple.from(args));
          },
          {
            all() {
              cache.clear();
            },
          }
        ),
      }
    );
  }

  export const debounce = <Fn extends UFunction.Any>(fn: Fn, wait: number) => {
    let timeout: NodeJS.Timeout;
    let pending: (() => void) | undefined;

    const res = Object.assign(
      (...args: Parameters<Fn>) => {
        clearTimeout(timeout);
        pending = () => {
          fn(...args);
          pending = undefined;
        };
        timeout = setTimeout(pending, wait);
      },
      // Pass through properties attached to fn
      fn,
      {
        immediate(...args: Parameters<Fn>): ReturnType<Fn> {
          res.clear();
          return fn(...args);
        },
        flush() {
          clearTimeout(timeout);
          pending?.();
        },
        clear() {
          clearTimeout(timeout);
          pending = undefined;
        },
      }
    );

    return res;
  };

  export const throttle = <Fn extends UFunction.Any>(
    fn: Fn,
    interval: number
  ) => {
    let timeout: NodeJS.Timeout | undefined;
    let pending: (() => void) | undefined;

    return Object.assign(
      ((...args) => {
        pending = () => {
          fn(...args);
          pending = undefined;
          timeout = setTimeout(() => {
            timeout = undefined;
            pending?.();
          }, interval);
        };
        if (!timeout) pending();
      }) as Fn,
      {
        flush() {
          clearTimeout(timeout);
          timeout = undefined;
          pending?.();
        },
      }
    );
  };

  export const unwrap = <T, Args extends any[]>(
    fn: UFunction.Maybe<Args, T>,
    ...args: Args
  ): T => (typeof fn === "function" ? (fn as UFunction.Any)(...args) : fn);

  export const noop = () => {};
  export const identity = <T,>(value: T) => value;
  export const todo = () => {
    throw new UError.Unimplemented();
  };

  export const withArgs = <
    Args extends any[],
    Fn extends (...args: Args) => any,
  >(
    fn: Fn,
    ...args: Args
  ) => [fn, args];

  // This is only correct for functions without optional arguments, afaik
  // supporting optional arguments is not possible
  export type HasArgCountOf<
    Fn extends Any,
    Count extends number,
  > = HasArgCountOfImpl<Fn, UTuple.Of<any, Count>>;
  type HasArgCountOfImpl<Fn extends Any, Args extends any[]> = Fn extends (
    ...args: Args
  ) => any
    ? Args extends [any, ...infer Rest]
      ? // If Fn extends a function with a lower arg count, then it does not have
        // exactly `Count` args
        HasArgCountOfImpl<Fn, Rest> extends true
        ? false
        : true
      : true
    : false;

  export const hasArgCountOf = <Fn extends Any, Count extends number>(
    fn: Fn,
    count: Count
  ): fn is Fn extends any
    ? HasArgCountOf<Fn, Count> extends true
      ? Fn
      : never
    : never => fn.length === count;
}

export default UFunction;
